import { EntityRefUtil, formatDateWithUserTimezone, GetUsersResponseDTO, PermissionRoles, UserStatus } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { TooltipArrow, TooltipPortal } from '@radix-ui/react-tooltip';
import { AccessorKeyColumnDef, createColumnHelper } from '@tanstack/react-table';
import { match } from 'ts-pattern';

import SendIcon from '@/components/icons/send';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { UserAvatarLabel } from '@/components/users/user-avatar-label';
import { User } from '@/legacy/models/user';
import { cn, initials } from '@/lib/utils';
import { RoleCell } from '@/pages/users/contributors/role-cell';
import { useUserStore } from '@/stores/user.store';
import { getAvatarBackgroundColorClass } from '@/utils/avatar';
import { getGearbeltRole } from '@/utils/permissions';

// Add custom options interface
interface UserTableOptions {
  onReinvite?: (user: User) => void;
  /** Current actor's resolved roles. Members see role as a static pill; Admins see a dropdown. */
  currentUserRoles?: PermissionRoles[];
  /** Current actor's user id. Used to disable the dropdown on their own row. */
  currentUserId?: string;
}

const columnHelper = createColumnHelper<GetUsersResponseDTO['data'][number]>();

export const createGearbeltContributorsTableColumns = (options?: UserTableOptions) => {
  const userTimezone = useUserStore.getState().user?.timezone;
  const { onReinvite, currentUserRoles, currentUserId } = options || {};

  return [
    columnHelper.display({
      id: 'select',
      header: ({ table }) => (
        <div className="flex w-full justify-center">
          <label className="-m-2 p-2">
            <Checkbox
              checked={table.getIsAllPageRowsSelected()}
              onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
              aria-label="Select all"
            />
          </label>
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex w-full justify-center">
          <label className="-m-2 p-2" onClick={(e) => e.stopPropagation()}>
            <Checkbox checked={row.getIsSelected()} onCheckedChange={(value) => row.toggleSelected(!!value)} aria-label="Select row" />
          </label>
        </div>
      ),
      meta: {
        cellClassName: 'w-[40px] min-w-[40px] max-w-[40px]',
      },
      enableSorting: false,
      enableHiding: false,
    }),
    columnHelper.accessor('firstName', {
      id: 'firstName',
      header: 'First Name',
      enableGrouping: false,
      enableColumnFilter: false,
      cell: ({ row, table }) => {
        const user = row.original;
        const value = user.firstName || '-';
        const searchTerm = table.getState().globalFilter;
        const avatarBackgroundColorClass = getAvatarBackgroundColorClass(user.firstName || '');

        const userFullInitials = initials(EntityRefUtil.createUserFullNameRef(user));
        return (
          <UserAvatarLabel
            value={value}
            operatedByFullInitials={userFullInitials}
            avatarUrl={user.avatarUrl || ''}
            avatarBackgroundColorClass={avatarBackgroundColorClass}
            searchTerm={searchTerm}
          />
        );
      },
    }),
    columnHelper.accessor('lastName', {
      id: 'lastName',
      header: 'Last Name',
      enableGrouping: false,
      enableColumnFilter: false,
      cell: ({ getValue, table }) => {
        const value = getValue() || '-';
        const searchTerm = table.getState().globalFilter;
        return (
          <div className={cn('overflow-hidden overflow-ellipsis', value === '-' && 'text-secondary-text')}>
            {searchTerm ? highlightSearchMatch(value, searchTerm) : value}
          </div>
        );
      },
    }),
    columnHelper.accessor('email', {
      id: 'email',
      header: 'Email',
      enableGrouping: false,
      enableColumnFilter: false,
      cell: ({ getValue, table }) => {
        const value = getValue() || '-';
        const searchTerm = table.getState().globalFilter;
        return (
          <div className={cn('overflow-hidden overflow-ellipsis', value === '-' && 'text-secondary-text')}>
            {searchTerm ? highlightSearchMatch(value, searchTerm) : value}
          </div>
        );
      },
    }),
    columnHelper.accessor('roles', {
      id: 'permission',
      header: 'Permission',
      enableGrouping: false,
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => {
        const user = row.original;
        const isAdminOrHigher = currentUserRoles?.includes(PermissionRoles.GearbeltAdmin) || currentUserRoles?.includes(PermissionRoles.SitemateSuperUser);
        const editable = Boolean(isAdminOrHigher) && user.id !== currentUserId;
        return <RoleCell userId={user.id} role={getGearbeltRole(user.roles)} editable={editable} />;
      },
      meta: {
        cellClassName: 'w-[140px]',
      },
    }),
    columnHelper.accessor('lastActive', {
      id: 'lastActive',
      header: 'Last Active',
      enableGrouping: false,
      enableColumnFilter: false,
      cell: ({ getValue }) => {
        const dateValue = getValue();
        const value = (dateValue && formatDateWithUserTimezone(new Date(dateValue), userTimezone)) || '-';
        return <div className={cn('overflow-hidden overflow-ellipsis', value === '-' && 'text-secondary-text')}>{value}</div>;
      },
      meta: {
        cellClassName: 'w-[120px]',
        sortTooltip: {
          asc: 'Oldest to Newest',
          desc: 'Newest to Oldest',
        },
      },
    }),
    columnHelper.accessor('createdAt', {
      id: 'createdAt',
      header: 'Date Joined',
      enableGrouping: false,
      enableColumnFilter: false,
      cell: ({ getValue }) => {
        const dateValue = getValue();
        const value = (dateValue && formatDateWithUserTimezone(new Date(dateValue), userTimezone)) || '-';
        return <div className={cn('overflow-hidden overflow-ellipsis', value === '-' && 'text-secondary-text')}>{value}</div>;
      },
      meta: {
        cellClassName: 'w-[120px]',
        sortTooltip: {
          asc: 'Oldest to Newest',
          desc: 'Newest to Oldest',
        },
      },
    }),
    columnHelper.accessor('invitedBy', {
      id: 'invitedBy',
      header: 'Invited By',
      enableGrouping: false,
      enableColumnFilter: false,
      cell: ({ row }) => {
        const user = row.original;
        const value = EntityRefUtil.createUserFullNameRef(user.invitedBy, '-');
        const avatarBackgroundColorClass = getAvatarBackgroundColorClass(user.invitedBy?.firstName || '');

        const userFullInitials = initials(EntityRefUtil.createUserFullNameRef(user.invitedBy));

        return (
          <UserAvatarLabel
            value={value}
            operatedByFullInitials={userFullInitials}
            avatarUrl={user.invitedBy?.avatarUrl || ''}
            avatarBackgroundColorClass={avatarBackgroundColorClass}
          />
        );
      },
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: 'Status',
      enableGrouping: false,
      enableColumnFilter: false,
      cell: ({ row }) => {
        const user = row.original;

        const badgeClasses = match(user.status as UserStatus)
          .with(UserStatus.Active, () => 'bg-default-light-green text-default-dark-green')
          .with(UserStatus.Pending, () => 'bg-default-light-orange text-default-dark-orange')
          .otherwise(() => 'bg-grey-1 text-default-text');

        const statusText = match(user.status as UserStatus)
          .with(UserStatus.Active, () => 'Active')
          .with(UserStatus.Pending, () => 'Pending')
          .otherwise(() => 'bg-grey-1 text-default-text');

        const displayValue = (
          <div className="flex flex-row gap-5">
            <span className={cn('flex w-fit items-center gap-5 rounded-[5px] p-[2px_5px] text-sm font-medium', badgeClasses)}>{statusText}</span>
            {user.status === UserStatus.Pending && currentUserRoles?.includes(PermissionRoles.GearbeltAdmin) ? (
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger type="button">
                    <Button
                      type="button"
                      variant={'grey'}
                      aria-label="Resend Invite"
                      className="flex w-fit items-center gap-5 rounded-[5px] p-[2px_5px] text-sm font-medium"
                      onClick={() => {
                        onReinvite?.(user as unknown as User);
                      }}>
                      <SendIcon />
                    </Button>
                  </TooltipTrigger>
                  <TooltipPortal>
                    <TooltipContent side="bottom" sideOffset={0} className="bg-default-text max-w-[200px] border-none p-1 text-xs text-white">
                      <TooltipArrow className="fill-default-text" />
                      Resend Invite
                    </TooltipContent>
                  </TooltipPortal>
                </Tooltip>
              </TooltipProvider>
            ) : (
              ''
            )}
          </div>
        );
        return displayValue;
      },
      meta: {
        cellClassName: 'w-[120px]',
      },
    }),
  ] as AccessorKeyColumnDef<GetUsersResponseDTO['data'][number]>[];
};

function highlightSearchMatch(text: string, searchTerm: string) {
  if (!text || !searchTerm) return text;
  const regex = new RegExp(`(${searchTerm})`, 'gi');
  return text.split(regex).map((part) => (regex.test(part) ? <b>{part}</b> : part));
}
